import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/services/apiClient";
import {
  fetchMapLots,
  type MapLotsParams,
  type MapLotsResponse,
} from "../api/lots";
import { createLogger } from "../../../utils/logger";

type ViewportParams = {
  bbox: string;
  zoom: number;
  clientWidth?: number;
  clientHeight?: number;
  preferClusters?: boolean;
};

type UseMapLotsDataOptions = {
  accessToken: string | null;
  workspaceIds?: string[];
  includeArchived?: boolean;
  debug?: boolean;
  messages: {
    defaultError: string;
    viewportTooLarge: string;
  };
};

const emptyPayload: MapLotsResponse = {
  clusters: [],
  lots: [],
  exceeded_max_results: false,
};

export function useMapLotsData({
  accessToken,
  workspaceIds,
  includeArchived = false,
  debug = (import.meta.env.MODE ?? "").toLowerCase() !== "production",
  messages,
}: UseMapLotsDataOptions) {
  const [data, setData] = useState<MapLotsResponse>(emptyPayload);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const logger = useMemo(() => createLogger("LotMapData"), []);
  const debugLog = useCallback(
    (...args: unknown[]) => {
      if (!debug) return;
      logger.debug(...args);
    },
    [debug, logger],
  );

  const fetchData = useCallback(
    async (viewport: ViewportParams) => {
      if (!accessToken) return;
      const normalizedZoom = Number.isFinite(viewport.zoom)
        ? Math.max(0, Math.round(viewport.zoom))
        : 0;
      if (abortRef.current) {
        abortRef.current.abort();
        debugLog("Aborted previous map lots request");
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      try {
        const params: MapLotsParams = {
          bbox: viewport.bbox,
          zoom: normalizedZoom,
          workspace_ids: workspaceIds,
          include_archived: includeArchived,
          client_width_px: viewport.clientWidth,
          client_height_px: viewport.clientHeight,
          prefer_clusters: viewport.preferClusters ?? true,
        };
        debugLog("Fetching map lots", params);
        const payload = await fetchMapLots(accessToken, params, {
          signal: controller.signal,
        });
        setData(payload ?? emptyPayload);
        setHasLoaded(true);
        setError(null);
        debugLog("Map lots response", {
          clusters: payload?.clusters?.length ?? 0,
          lots: payload?.lots?.length ?? 0,
          exceeded: payload?.exceeded_max_results ?? false,
        });
      } catch (err) {
        if (controller.signal.aborted) {
          debugLog("Map lots request aborted by controller");
          return;
        }
        const apiError = err as ApiError;
        let message = messages.defaultError;
        if (apiError?.status === 400) {
          message = messages.viewportTooLarge;
        }
        setData(emptyPayload);
        setHasLoaded(true);
        setError(message);
        debugLog("Map lots request failed", { message, status: apiError?.status, error: err });
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setIsLoading(false);
      }
    },
    [accessToken, debugLog, includeArchived, messages, workspaceIds],
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { data, isLoading, error, fetchData, setData, setError, hasLoaded };
}
